import Styles from './CaptionTab.module.scss'
import { Tone } from 'components/DocRightRail/screens/AIAssistant/ToneSelect'
import { TabPanel } from '../TabPanel/TabPanel'
import { useBrandVoices } from 'hooks/useBrandVoices'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useEffect, useMemo, useState } from 'react'
import { AI, Edit1 } from 'blaze-ui/icons'
import { useModals } from 'almanac-ui/Modal'
import { useOpenBrandVoiceModal } from 'screens/BrandKits/components/BrandVoice/useOpenBrandVoiceModal'
import { AnalysingContentModal } from 'screens/BrandKits/components/BrandVoice/AddVoice/AnalysingContentModal'
import { EditVoiceModal } from 'screens/BrandKits/components/BrandVoice/EditVoiceModal'
import { SegmentedControl } from 'blaze-ui/SegmentedControl/SegmentedControl'
import { CaptionWriteYourOwn } from './CaptionWriteYourOwn'
import { CaptionGenerateWithAI } from './CaptionGenerateWithAI'
import { CaptionsStore } from 'stores/CaptionsStore'
import { CaptionEdit } from './CaptionEdit'
import { Item } from '@react-stately/collections'
import { useOpenBrandKitCreationModal } from 'hooks/useOpenBrandKitCreationModal'
import { DesignChannel } from 'components/NewFileModal/Types'

interface Props {
  captionsStore: CaptionsStore
  customPrompt: string
  setCustomPrompt: (prompt: string) => void
  voice?: number
  tones: Tone[]
  language: string
  selectVoice: (voice?: number) => void
  selectTone: (tone: Tone) => void
  setLanguage: (language: string) => void
  generate: () => void
  insertCaption: (caption: string) => void
  removeCaption: () => void
}

export const CaptionGenerationForm = observer(
  ({
    captionsStore,
    customPrompt,
    setCustomPrompt,
    voice,
    tones,
    language,
    selectVoice,
    selectTone,
    setLanguage,
    generate,
    insertCaption,
    removeCaption,
  }: Props) => {
    const { designStore, drawersStore } = useRootStore()
    const currentWorkspace = useCurrentWorkspace()
    const brandKitEnabled = useFlipperFlag('brand_kits')

    const channel = designStore?.file?.channel as DesignChannel | undefined

    const caption = captionsStore.caption

    const [manualCaption, setManualCaption] = useState(caption)
    const [mode, setMode] = useState<'ai' | 'write'>('ai')
    const [regenerating, setRegenerating] = useState(false)

    const { openModal, closeModals, closeStack } = useModals()
    const openBrandVoiceModal = useOpenBrandVoiceModal()

    const { data: allVoices, isLoading: voicesLoading } = useBrandVoices(currentWorkspace)
    const { data: allBrandKits, isLoading: kitsLoading } = useBrandKits()
    const isLoading = voicesLoading || kitsLoading

    const brandKits = useMemo(() => allBrandKits?.filter((bk) => !!bk.brand_voice) ?? [], [allBrandKits])
    const voices = useMemo(() => allVoices ?? [], [allVoices])

    const { openBrandKitCreationModal } = useOpenBrandKitCreationModal({ newStack: true })

    useEffect(() => {
      if (isLoading || mode === 'write' || voice || tones.length > 0) {
        return
      }

      if (brandKitEnabled) {
        const kitVoice = brandKits[0]?.brand_voice
        if (kitVoice) {
          selectVoice(kitVoice.id)
        }
      } else if (voices.length > 0) {
        selectVoice(voices[0].id)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [brandKits, voices, voice, tones, brandKitEnabled, mode, isLoading, caption])

    const openAddBrandKitModal = () => {
      drawersStore.closeAllDrawers()
      openBrandKitCreationModal()
    }

    const openAddVoiceModal = () => {
      if (!currentWorkspace) return

      drawersStore.closeAllDrawers()
      openBrandVoiceModal(
        {
          onAnalyse: (analyseProps) =>
            openModal(AnalysingContentModal, {
              ...analyseProps,
              onNext: (editProps) =>
                openModal(EditVoiceModal, {
                  ...editProps,
                  onBack: () => {
                    closeModals(2)
                  },
                  onCreate: ({ voiceId }) => {
                    closeStack()
                    selectVoice(voiceId)
                  },
                }),
            }),
        },
        { newStack: true },
      )
    }

    const renderContent = () => {
      if (caption && !regenerating) {
        return (
          <CaptionEdit
            caption={manualCaption}
            setCaption={setManualCaption}
            insertCaption={() => insertCaption(manualCaption)}
            removeCaption={removeCaption}
            setRegenerating={setRegenerating}
            channel={channel}
          />
        )
      }

      return (
        <>
          <div className={Styles.toggle}>
            <SegmentedControl
              className={Styles.toggleElement}
              onItemClick={(name) => setMode(name as 'ai' | 'write')}
              activeItem={mode}
              variant="secondary"
              rounded
            >
              <Item key="ai" textValue="Generate">
                <AI />
                Generate
              </Item>
              <Item key="write" textValue="Write Your Own">
                <Edit1 />
                Write Your Own
              </Item>
            </SegmentedControl>
          </div>

          {mode === 'write' ? (
            <CaptionWriteYourOwn insertCaption={insertCaption} />
          ) : (
            <CaptionGenerateWithAI
              customPrompt={customPrompt}
              setCustomPrompt={setCustomPrompt}
              brandKits={brandKits}
              voices={voices}
              voice={voice}
              tones={tones}
              language={language}
              setLanguage={setLanguage}
              selectTone={selectTone}
              selectVoice={selectVoice}
              openAddBrandKitModal={openAddBrandKitModal}
              openAddVoiceModal={openAddVoiceModal}
              onSubmit={generate}
            />
          )}
        </>
      )
    }

    return (
      <TabPanel.Root>
        <TabPanel.Header
          onBack={() => designStore?.openDefaultTab()}
          title={captionsStore?.sectionTitle ?? 'Caption'}
        />
        <TabPanel.Body>{renderContent()}</TabPanel.Body>
      </TabPanel.Root>
    )
  },
)
